import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { DeferredPromise, sleep } from '@cf-shared/shared-util';
import { AnimationController } from 'providers/animation-controller';
import { TutorialController } from 'providers/tutorial-controller';
import { animationFrame, DestroyGuard } from 'services/browser-utils';
import { DatabaseWrapper , DocWatcher } from 'services/database-wrapper';
import { SettingsService } from 'services/settings.service';
import { UserDb } from 'services/user-db.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';

export interface TutorialStep {
  message: TranslatedString;
  /// The step will highlight the bounding box of all these css selectors.
  highlightSelectors: string[];
  /// with this, we don't wait for all selectors to start animating (useful for multiple lists where you don't know which one will be used)
  allowUnmatchedSelectors?: boolean;
  /// if not specified, the default will enlarge the ellipse a bit to try to capture most of the bounding box
  highlightScaler?: number;
  onBeginAsync?: () => Promise<void>;
  onCloseAsync?: () => Promise<void>;
}

export interface Tutorial {
  // used to save in profile to know if the tutorial was already seen.
  id: string | 'ALWAYS'; // ALWAYS means always show it
  steps: TutorialStep[];
}

function pixels(x: number) {
  return `${x.toFixed(2)}px`;
}

@Component({
  selector: 'tutorial-host',
  templateUrl: 'tutorial-host.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TutorialHostComponent implements OnDestroy {
  private destroyGuard = new DestroyGuard();
  private docWatcher: DocWatcher;
  @ViewChild('main') mainElem: ElementRef;
  currentStep: TutorialStep = undefined;
  currentMessage: TranslatedString = untranslatedString('');
  textVisible = false;

  constructor(
    private tutorialCtrl: TutorialController,
    private userDb: UserDb,
    private settings: SettingsService,
    private animCtrl: AnimationController,
    private cdr: ChangeDetectorRef,
  ) {
    this.tutorialCtrl.registerHost(this);
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    this.docWatcher?.destroy();
  }

  currentFrame = {
    cx: 0,
    cy: 0,
    rx: 0,
    ry: 0,
  };

  positionAnimationPromise: Promise<void> = undefined;

  // adjust positions of other tutorials when a new one is added or removed
  async animateTutorialPositionsAsync() {
    if (!this.currentStep) {
      return;
    }

    if (this.positionAnimationPromise === undefined) {
      this.positionAnimationPromise = this.animateTutorialPositions_internalAsync();
    }

    return this.positionAnimationPromise;
  }
  async animateTutorialPositions_internalAsync() {

    if (!this.currentStep) {
      this.positionAnimationPromise = undefined;
      return;
    }

    let startTime = await animationFrame();
    let lastTime = startTime;
    let animating = true;
    while (animating) {
      let nowTime = await animationFrame();
      // if tutorial was dismissed during the animation frame yield
      if (!this.currentStep) {
        this.positionAnimationPromise = undefined;
        return;
      }
      if (nowTime - startTime > 1000) {
        // stop any animations that are too long (probably stuck)
        animating = false;
      }
      let timeStep = (nowTime - lastTime) / 1000;
      timeStep = Math.min(Math.max(timeStep, 0.001), 1);  // clamp to reasonable values, just in case
      lastTime = nowTime;

      // prepare interpolation factors
      const dampingPercent = 0.001;
      const dampingTime = 0.5;
      let dampingFactor = Math.log(1 / dampingPercent) / dampingTime;
      let attenuator = Math.exp(-timeStep * dampingFactor);
      let attenuate = (x: number, targetX: number) => targetX + (x - targetX) * attenuator;
      let isClose = (x: number, x2: number) => Math.abs(x2 - x) < Math.max(dampingPercent * Math.abs(x2), 1);

      // get the positions of the entire page
      let mainElem = this.mainElem.nativeElement as HTMLElement;
      let pageW = mainElem.offsetWidth;
      let pageH = mainElem.offsetHeight;

      // get the bounding box to highlight
      let minx = Number.MAX_VALUE;
      let miny = Number.MAX_VALUE;
      let maxx = -Number.MAX_VALUE;
      let maxy = -Number.MAX_VALUE;
      let someSelectorNotFound = false;
      let atLeastOneSelectorFound = false;
      for (let selector of this.currentStep.highlightSelectors) {
        let elem = document.querySelector(selector) as HTMLElement;
        if (!elem) {
          someSelectorNotFound = true;
          continue;
        }
        atLeastOneSelectorFound = true;
        let rect = elem.getBoundingClientRect();
        minx = Math.min(minx, rect.left);
        miny = Math.min(miny, rect.top);
        maxx = Math.max(maxx, rect.right);
        maxy = Math.max(maxy, rect.bottom);
      }
      // if no selector was found, don't animate yet
      if (!atLeastOneSelectorFound) {
        continue;
      }
      // if some selector didn't appear yet, that will animate only if explicitly allowed
      if (someSelectorNotFound && !this.currentStep.allowUnmatchedSelectors) {
        // otherwise, wait for all selectors to appear (cound be the page is still loading, etc)
        continue;
      }
      let l = minx;
      let t = miny;
      let w = maxx - minx;
      let h = maxy - miny;

      // position of the target ellipse
      let scale = this.currentStep.highlightScaler || 1.5;
      let cx = l + w / 2;
      let cy = t + h / 2;
      let rx = w / 2 * scale;
      let ry = h / 2 * scale;

      // animate towards the target ellipse
      this.currentFrame.cx = attenuate(this.currentFrame.cx, cx);
      this.currentFrame.cy = attenuate(this.currentFrame.cy, cy);
      this.currentFrame.rx = attenuate(this.currentFrame.rx, rx);
      this.currentFrame.ry = attenuate(this.currentFrame.ry, ry);

      // if near the target
      if (isClose(this.currentFrame.cx, cx)
        && isClose(this.currentFrame.cy, cy)
        && isClose(this.currentFrame.rx, rx)
        && isClose(this.currentFrame.ry, ry)) {
        // snap to it
        this.currentFrame.cx = cx;
        this.currentFrame.cy = cy;
        this.currentFrame.rx = rx;
        this.currentFrame.ry = ry;

        /// find the text position....
        let margin = 20;
        let maxTextWidth = 400;
        // if the target is in the upper half
        if (t + h / 2 < pageH / 2) {
          // position the text below the target
          mainElem.style.setProperty('--tt', pixels(t + h + margin));
          // the text box reaches down to the screen's bottom edge, so it must stop above the
          // system navigation bar the native apps draw under (#5810)
          mainElem.style.setProperty('--tb',
            `calc(${pixels(margin)} + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))`);

          // if the target is in the bottom half
        } else {
          // position the text above the target
          mainElem.style.setProperty('--tt', `unset`);
          mainElem.style.setProperty('--tb', pixels(pageH - (t - margin)));
        }

        // center the text on the target
        let tl = l + w / 2 - maxTextWidth / 2;
        let tr = tl + maxTextWidth;
        // clamp it to borders while trying to maintain width
        if (tr > pageW - margin) {
          tr = pageW - margin;
          tl = Math.max(margin, tr - maxTextWidth);
        }
        if (tl < margin) {
          tl = margin;
          tr = Math.min(tl + maxTextWidth, pageW - margin);
        }
        mainElem.style.setProperty('--tl', pixels(tl));
        mainElem.style.setProperty('--tr', pixels(pageW - tr));

        // and mark the end of animation
        animating = false;
      }

      // position the highlight at the current animation frame
      mainElem.style.setProperty('--cx', pixels(this.currentFrame.cx));
      mainElem.style.setProperty('--cy', pixels(this.currentFrame.cy));
      mainElem.style.setProperty('--rx', pixels(this.currentFrame.rx));
      mainElem.style.setProperty('--ry', pixels(this.currentFrame.ry));
    }
    // when arrived at the final position...
    this.positionAnimationPromise = undefined;
  }

  ngAfterViewInit() {
  }

  // reapply the layout when the window is resized
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    void (event);
    void this.animateTutorialPositionsAsync();
  }

  clickSignal: DeferredPromise<void> = undefined;

  async playTutorialAsync(tutorial: Tutorial) {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.destroyGuard.isDestroyed) return;
        this.cdr.markForCheck();
      });
    }

    if (await this.alreadySeenAsync(tutorial.id)) {
      return;
    }

    // before showing anything, initialize to default position (in case used before)
    this.currentFrame = { cx: 0, cy: 0, rx: 0, ry: 0 }
    let mainElem = this.mainElem.nativeElement as HTMLElement;
    mainElem.style.setProperty('--cx', pixels(this.currentFrame.cx));
    mainElem.style.setProperty('--cy', pixels(this.currentFrame.cy));
    mainElem.style.setProperty('--rx', pixels(this.currentFrame.rx));
    mainElem.style.setProperty('--ry', pixels(this.currentFrame.ry));

    for (let step of tutorial.steps) {
      if (this.destroyGuard.isDestroyed) return;
      this.currentStep = step;
      this.cdr.markForCheck();

      if (step.onBeginAsync !== undefined) {
        await step.onBeginAsync();
      }
      if (this.destroyGuard.isDestroyed) return;

      await this.animCtrl.withAnimationTracking(async () => {
        // show text immediately and start animation in parallel with fixed timeout
        this.currentMessage = step.message;
        this.textVisible = true;
        if (!this.destroyGuard.isDestroyed) {
          this.cdr.markForCheck();
        }

        void this.animateTutorialPositionsAsync(); // animation runs independently
        await sleep(500); // fixed 500ms before step can be dismissed
      });
      if (this.destroyGuard.isDestroyed) return;

      // wait for the click
      this.clickSignal = DeferredPromise.defer<void>();
      await this.clickSignal;
      if (this.destroyGuard.isDestroyed) return;
      // disappear the text
      this.textVisible = false;
      this.cdr.markForCheck();

      // eventual close callback runs in parallel with text disappearing, but we don't wait for text disappearing, next animation can start before it is finished
      if (step.onCloseAsync !== undefined) {
        await step.onCloseAsync();
      }
    }

    // only marked as seen after all steps are done
    await this.markSeenAsync(tutorial.id);
    if (this.destroyGuard.isDestroyed) return;
    this.currentStep = undefined;
    this.currentMessage = untranslatedString('');
    this.cdr.markForCheck();
  }

  // check if a tutorial was already seen
  async alreadySeenAsync(id: string): Promise<boolean> {
    if (id === "ALWAYS") {
      return false;
    }
    let fitnessPlan = await this.userDb.loadPlanAsync(this.docWatcher);
    return fitnessPlan.tutorialsSeen && fitnessPlan.tutorialsSeen.includes(id) && !this.settings.debugShowAllTutorials;
  }
  async markSeenAsync(id: string) {
    if (id === "ALWAYS") {
      return;
    }
    let fitnessPlan = await this.userDb.loadPlanAsync(this.docWatcher);
    if (!fitnessPlan.tutorialsSeen) {
      fitnessPlan.tutorialsSeen = [];
    }
    if (!fitnessPlan.tutorialsSeen.includes(id)) {
      fitnessPlan.tutorialsSeen.push(id);
    }
    await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
  }

  onClick() {
    if (this.clickSignal) {
      this.clickSignal.resolve(undefined);
    }
  }
}
